import React, { useEffect, useLayoutEffect, useMemo, useRef, useState, useId } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, X } from 'lucide-react';

export type ProfileComboOption = { value: string; label: string };

type PanelPlacement = 'above' | 'below';

type PortalCoords = {
  top: number;
  left: number;
  width: number;
  placement: PanelPlacement;
};

const PANEL_VIEWPORT_GAP = 4;
/** Matches `max-h-72` — used to pick above/below before the panel mounts. */
const PANEL_ESTIMATED_MAX_HEIGHT = 288;

/** Custom dropdown (button + panel) for profile fields — avoids native `<select>` UI on mobile. */
export function ProfileComboList({
  value,
  onChange,
  options,
  searchable,
  searchPlaceholder = 'Search…',
  placeholder = 'Choose…',
  id,
  'aria-label': ariaLabel,
  accentSelectedClass = 'bg-purple-50 dark:bg-purple-950/40 text-purple-900 dark:text-purple-100',
  accentCheckClass = 'text-purple-600 dark:text-purple-400',
  className = '',
  triggerClassName = '',
  useFixedPortal = false,
  /** When false, the panel stays open until the chevron is clicked or an option is chosen. */
  closeOnOutsideClick = true,
  /** Top-right close control on the dropdown panel (defaults to true when `closeOnOutsideClick` is false). */
  showPanelCloseButton,
  disabled = false,
  /** Controlled open state (use with `onOpenChange` to coordinate multiple lists in one form). */
  open: openProp,
  onOpenChange,
  size = 'md',
}: {
  value: string;
  onChange: (value: string) => void;
  options: readonly ProfileComboOption[];
  searchable?: boolean;
  searchPlaceholder?: string;
  placeholder?: string;
  id?: string;
  'aria-label'?: string;
  accentSelectedClass?: string;
  accentCheckClass?: string;
  /** Extra classes on the outer wrapper (e.g. z-index). */
  className?: string;
  /** Merged onto the trigger button (e.g. modal field borders). */
  triggerClassName?: string;
  /** Render the panel in `document.body` with `position: fixed` so scroll parents (e.g. edit profile modal) do not clip it. */
  useFixedPortal?: boolean;
  closeOnOutsideClick?: boolean;
  showPanelCloseButton?: boolean;
  disabled?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** `sm` matches compact form fields (Leitner add tab); default matches Edit profile. */
  size?: 'md' | 'sm';
}) {
  const panelCloseVisible = showPanelCloseButton ?? !closeOnOutsideClick;
  const isCompact = size === 'sm';
  const reactId = useId();
  const listboxId = id ? `${id}-listbox` : `${reactId}-listbox`;
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : internalOpen;

  const setOpen = (next: boolean) => {
    if (!isControlled) setInternalOpen(next);
    onOpenChange?.(next);
  };
  const [q, setQ] = useState('');
  const [portalCoords, setPortalCoords] = useState<PortalCoords | null>(null);
  const [panelPlacement, setPanelPlacement] = useState<PanelPlacement>('below');
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const resolvePanelPlacement = (triggerRect: DOMRect, panelHeight?: number): PanelPlacement => {
    const estimatedHeight = panelHeight ?? PANEL_ESTIMATED_MAX_HEIGHT;
    const spaceBelow = window.innerHeight - triggerRect.bottom - PANEL_VIEWPORT_GAP;
    const spaceAbove = triggerRect.top - PANEL_VIEWPORT_GAP;
    if (spaceBelow >= estimatedHeight) return 'below';
    if (spaceAbove >= estimatedHeight) return 'above';
    return spaceAbove > spaceBelow ? 'above' : 'below';
  };

  useEffect(() => {
    if (!disabled) return;
    setOpen(false);
    setQ('');
  }, [disabled]);

  useEffect(() => {
    if (!open) setQ('');
  }, [open]);

  useEffect(() => {
    if (!open || !closeOnOutsideClick) return;
    const onDoc = (e: MouseEvent | TouchEvent) => {
      const t = e.target as Node;
      if (rootRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
      setQ('');
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('touchstart', onDoc, { passive: true });
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('touchstart', onDoc);
    };
  }, [open, closeOnOutsideClick]);

  const closePanel = () => {
    setOpen(false);
    setQ('');
  };

  const openPanel = () => {
    if (disabled) return;
    setOpen(true);
  };

  const togglePanel = () => {
    if (disabled) return;
    if (open) closePanel();
    else openPanel();
  };

  const filtered = useMemo(() => {
    if (!searchable || !q.trim()) return [...options];
    const s = q.trim().toLowerCase();
    return options.filter((o) => o.value.toLowerCase().includes(s) || o.label.toLowerCase().includes(s));
  }, [options, searchable, q]);

  useLayoutEffect(() => {
    if (!open) {
      setPortalCoords(null);
      setPanelPlacement('below');
      return;
    }

    const update = () => {
      const b = buttonRef.current;
      if (!b) return;
      const r = b.getBoundingClientRect();
      const measuredHeight = panelRef.current?.getBoundingClientRect().height;
      const placement = resolvePanelPlacement(r, measuredHeight);
      setPanelPlacement(placement);

      if (useFixedPortal) {
        setPortalCoords({
          top: placement === 'below' ? r.bottom + PANEL_VIEWPORT_GAP : r.top - PANEL_VIEWPORT_GAP,
          left: r.left,
          width: r.width,
          placement,
        });
      }
    };

    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);

    const panelEl = panelRef.current;
    const ro =
      typeof ResizeObserver !== 'undefined' && panelEl
        ? new ResizeObserver(() => update())
        : null;
    ro?.observe(panelEl!);

    // Portal panel mounts one frame after coords are first set — re-measure for flip + ResizeObserver.
    let raf = 0;
    if (useFixedPortal) {
      raf = window.requestAnimationFrame(() => update());
    }

    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
      ro?.disconnect();
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [open, useFixedPortal, filtered.length, searchable]);

  const selected = options.find((o) => o.value === value);

  const panelBaseClass =
    'flex max-h-72 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl ring-1 ring-black/5 dark:border-gray-700 dark:bg-gray-900 dark:ring-white/10';

  const panelPositionClass = useFixedPortal
    ? 'fixed z-[10050]'
    : panelPlacement === 'above'
      ? 'absolute left-0 right-0 z-[300] bottom-full mb-1'
      : 'absolute left-0 right-0 z-[300] mt-1';

  const dropdownPanel = (
    <div
      key={listboxId}
      ref={panelRef}
      className={`${panelBaseClass} ${panelPositionClass}`}
      style={
        useFixedPortal && portalCoords
          ? {
              top: portalCoords.top,
              left: portalCoords.left,
              width: portalCoords.width,
              ...(portalCoords.placement === 'above' ? { transform: 'translateY(-100%)' } : {}),
            }
          : undefined
      }
    >
      {searchable ? (
        <div className="relative shrink-0 border-b border-gray-200 p-2 dark:border-gray-700">
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
            className={`w-full rounded-lg border border-gray-200 bg-gray-50 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-400/50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500 ${panelCloseVisible ? 'pl-3 pr-9' : 'px-3'}`}
            autoComplete="off"
            autoFocus
          />
          {panelCloseVisible && (
            <button
              type="button"
              onClick={closePanel}
              className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-1 text-gray-500 transition-colors hover:bg-gray-200/80 hover:text-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-100"
              aria-label="Close list"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          )}
        </div>
      ) : panelCloseVisible ? (
        <div className="flex shrink-0 items-center justify-end border-b border-gray-200 px-2 py-1 dark:border-gray-700">
          <button
            type="button"
            onClick={closePanel}
            className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-100"
            aria-label="Close list"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>
      ) : null}
      <ul id={listboxId} role="listbox" aria-label={ariaLabel} className="max-h-60 min-h-0 flex-1 overflow-y-auto overscroll-contain py-1">
        {filtered.length === 0 ? (
          <li className="px-3 py-2 text-center text-sm text-gray-500 dark:text-gray-400" role="presentation">
            No matches
          </li>
        ) : (
          filtered.map((o) => {
            const isOn = o.value === value;
            return (
              <li key={o.value} role="presentation">
                <button
                  type="button"
                  role="option"
                  aria-selected={isOn ? 'true' : 'false'}
                  className={`flex w-full items-center justify-between gap-2 text-left touch-manipulation hover:bg-gray-100 dark:hover:bg-gray-800 ${
                    isCompact ? 'px-2 py-1.5 text-xs' : 'px-3 py-2.5 text-sm'
                  } ${isOn ? accentSelectedClass : 'text-gray-800 dark:text-gray-200'}`}
                  onClick={() => {
                    onChange(o.value);
                    closePanel();
                  }}
                >
                  <span className="truncate">{o.label}</span>
                  {isOn && <Check className={`h-4 w-4 shrink-0 ${accentCheckClass}`} aria-hidden />}
                </button>
              </li>
            );
          })
        )}
      </ul>
    </div>
  );

  const showPortalPanel = useFixedPortal && open && portalCoords !== null;

  return (
    <div className={`relative ${className}`.trim()} ref={rootRef}>
      <div
        className={`flex w-full overflow-hidden border border-gray-200 bg-white hover:border-gray-300 focus-within:ring-2 focus-within:ring-purple-400/50 dark:border-gray-700 dark:bg-gray-900 dark:hover:border-gray-600 ${
          isCompact ? 'min-h-0 rounded shadow-none' : 'min-h-[44px] rounded-xl shadow-sm'
        } ${disabled ? 'cursor-not-allowed opacity-50' : ''} ${triggerClassName}`.trim()}
      >
        <button
          ref={buttonRef}
          type="button"
          id={id}
          aria-label={ariaLabel}
          aria-expanded={open ? 'true' : 'false'}
          aria-haspopup="listbox"
          aria-controls={listboxId}
          onClick={() => {
            if (disabled) return;
            if (closeOnOutsideClick) togglePanel();
            else if (!open) openPanel();
          }}
          disabled={disabled}
          className={`flex min-w-0 flex-1 touch-manipulation items-center text-left text-gray-900 focus:outline-none dark:text-gray-100 disabled:cursor-not-allowed ${
            isCompact ? 'px-2 py-1 text-xs' : 'px-3 py-2.5 text-sm'
          }`}
        >
          <span className={`truncate ${selected ? '' : 'text-gray-400 dark:text-gray-500'}`}>{selected?.label ?? placeholder}</span>
        </button>
        <button
          type="button"
          tabIndex={-1}
          aria-label={open ? 'Close list' : 'Open list'}
          onClick={(e) => {
            e.stopPropagation();
            togglePanel();
          }}
          disabled={disabled}
          className={`flex shrink-0 touch-manipulation items-center justify-center border-l border-gray-200 text-gray-400 hover:bg-gray-50 focus:outline-none dark:border-gray-600 dark:hover:bg-gray-800 disabled:cursor-not-allowed ${
            isCompact ? 'px-1.5 py-1' : 'px-2.5 py-2.5'
          }`}
        >
          <ChevronDown className={`transition-transform ${open ? 'rotate-180' : ''} ${isCompact ? 'h-3.5 w-3.5' : 'h-5 w-5'}`} aria-hidden />
        </button>
      </div>
      {open && !useFixedPortal && dropdownPanel}
      {showPortalPanel && typeof document !== 'undefined' && createPortal(dropdownPanel, document.body)}
    </div>
  );
}
